/* @flow */
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { withRouter, Redirect } from "react-router-dom";
import { getViewedStock } from "../../redux/reducer/tabs";
import { getActiveDeals } from "../../redux/reducer/deals";
import { getServerTime } from "../../redux/reducer/points";
import StatelessHeader from "../TradeHeader";
import Toolbar from "../Toolbar";
import StatelessChart from "../Chart";
import connected from "../common/connected";
import { mapStateToGettext } from "../common/Gettext";
import StatelessTradeControls from "../TradeControls";
import OrderSensitiveDesktopLayout from "../TradeDesktopLayout";
import StatelessInsufficientPopup from "../InsufficientPopup/InsufficientPopup";
import StatelessPopupDeposit from "../PopupDeposit/PopupDeposit";
import StatelessVerificationRequiredPopup from "../TradeModals/VerificationRequired/VerificationRequired";
import StatelessTradeIsClosingPopup from "../TradeModals/TradeIsClosing";
import StatelessIncreaseBidLimitPopup from "../TradeModals/IncreaseBidLimit";
import DemoTournamentPopup from "../TradeModals/DemoTournamentPopup";
import StatelessNews from "../News";
import StockClose from "./StockClose";
import Tour from "../Tour";
import withErrorCatch from "../withErrorCatch";

import type { State } from "../../redux/reducer";
import type { Dispatch } from "../../redux/action";
import type { LayoutProps } from "../TradingPlatform";

const InsufficientPopup = connect((state: State) => {
  const activeAccount = state.accounts.find(account => {
    return state.profile
      ? account.id === state.profile.active_account_id
      : account.isActive;
  });

  return {
    profileId: state.profile && state.profile.id,
    initialDeposit: state.remoteTradeSettings.initial,
    minFillup: state.remoteTradeSettings.minFillup,
    isActiveAccountReal: activeAccount ? activeAccount.type === "real" : false
  };
})(StatelessInsufficientPopup);

const PopupDeposit = connect((state: State) => {
  const activeAccount = state.accounts.find(account => {
    return state.profile
      ? account.id === state.profile.active_account_id
      : account.isActive;
  });

  return {
    profileId: state.profile && state.profile.id,
    accounts: state.accounts,
    initialDeposit: state.remoteTradeSettings.initial,
    minFillup: state.remoteTradeSettings.minFillup,
    isActiveAccountReal: activeAccount ? activeAccount.type === "real" : false
  };
})(StatelessPopupDeposit);

const VerificationRequiredPopup = connected(StatelessVerificationRequiredPopup)(
  (state: State) => ({
    validation: state.profile
      ? state.profile.validation
      : { phone: 0, email: 0, profile: 0, passport: 0 }
  })
);

const IncreaseBidLimitPopup = connected(StatelessIncreaseBidLimitPopup)(
  (state: State) => {
    const { profile } = state;

    let bid_limit = 5000;

    if (profile) {
      if (profile.bid_limit) {
        bid_limit =
          typeof profile.bid_limit === "object"
            ? profile.bid_limit[profile.currency]
            : profile.bid_limit;
      }
    }

    return {
      bid_limit: bid_limit
    };
  }
);

const TradeIsClosingPopup = connected(StatelessTradeIsClosingPopup)(
  (state: State) => {
    //
  }
);

const TradeHeader = withRouter(
  connect((state: State) => ({
    profile: state.profile,
    accounts: state.accounts
  }))(StatelessHeader)
);

const TradeControls = connect((state: State, props) => {
  const stock = getViewedStock(state.tabs);
  const points = state.points[stock] || [];
  let bids = state.remoteTradeSettings.bids;
  const currency = state.remoteTradeSettings.currency;
  let maxBidVolume;
  if (state.profile) {
    if (typeof state.profile.bid_limit === "object") {
      maxBidVolume = state.profile.bid_limit[currency];
    } else {
      maxBidVolume = state.profile.bid_limit;
    }
  }
  if (!maxBidVolume && state.apiConfigs) {
    maxBidVolume = state.apiConfigs.maxBidVolume[currency];
  }
  if (bids[bids.length - 1] > maxBidVolume) {
    bids = bids.filter(bid => bid <= maxBidVolume);
  }

  const openDeals = deal =>
    deal.time !== undefined && deal.profit === undefined;
  const stocksWithOperations = props.deals
    .filter(openDeals)
    .reduce((res, deal) => {
      if (deal) {
        res.push(deal.stock);
      }
      return res;
    }, []);

  const pointsByStock = stocksWithOperations.reduce((res, stock) => {
    const points = state.points[stock];
    if (points && !res[stock]) {
      res[stock] = points.length > 0 ? points[points.length - 1] : null;
    }

    return res;
  }, {});

  return {
    tradeMode: state.settings.mode,
    point: points.length > 0 ? points[points.length - 1] : null,
    pointsByStock,
    suggestedBids: bids,
    percentBidThreshold: state.remoteTradeSettings.percentBidThreshold,
    percentBids: state.remoteTradeSettings.percentBids,
    currentBid: state.settings.currentBid,
    maxBidVolume: maxBidVolume || bids[bids.length - 1],
    now: state.time,
    ...mapStateToGettext(state)
  };
})(StatelessTradeControls);

const Chart = connect((state: State, ownProps) => {
  const stock = getViewedStock(state.tabs);
  const time = getServerTime(stock, state.points) || 0;
  const deals =
    typeof ownProps.deals === "object" &&
    ownProps.deals.type === "SPECIAL TOUR DEAL"
      ? ownProps.deals.deals
      : getActiveDeals(stock, time, ownProps.deals);

  return {
    graphicalMode: state.graphicalMode,
    key: stock,
    tradeMode: state.settings.mode,
    points: ownProps.points || state.points[stock] || [],
    settings: { ...state.settings, ...ownProps.settings },
    stats: state.stats[stock],
    profile: state.profile,
    deals: deals
  };
})(StatelessChart);

const News = connect(
  (state: State) => ({
    news: state.news,
    soundsEnabled: state.audio.enabled
  }),
  (dispatch: Dispatch) => ({
    onDismiss(id) {
      dispatch({
        type: "DISMISS_NEWS",
        payload: id
      });
    }
  })
)(StatelessNews);

type IntentProps = {
  intent: "CAST_UP" | "CAST_DOWN" | ""
};

class DesktopTradeScreen extends PureComponent<$FlowFixMeProps, IntentProps> {
  chartRef = React.createRef(null);
  state = {
    intent: ""
  };

  onIntentUpdate = (intent: string) => {
    this.setState({ intent });
  };

  render() {
    const props = this.props;
    const isStockClose =
      !props.stock.enabled || props.timeUntilNextLockToggle === 0;

    return (
      <OrderSensitiveDesktopLayout
        headerHeight={127}
        sidebarWidth={174}
        dealsToCompensate={props.dealsToCompensate}
        onCompensation={props.onCompensation}
        compensationUpdated={props.compensationUpdated}
        compensationCreated={props.compensationCreated}
        onCompensationCreated={props.onCompensationCreated}
        activeAccount={props.activeAccount}
      >
        <TradeHeader
          isRefillFetching={props.isRefillFetching}
          isFetchingOpenOperation={props.isFetchingOpenOperation}
          accountsReady={props.accountsReady}
          dealsReady={props.dealsReady}
          deals={props.deals}
          onProfileUpdate={props.refreshProfile}
          onRefillDemoClick={props.refillDemoAccount}
          compensationUpdated={props.compensationUpdated}
          compensationCreated={props.compensationCreated}
        />
        <Toolbar
          deals={props.deals}
          instruments={this.state.toolbarInstruments}
          onClose={() => this.setState({ toolbarInstruments: null })}
        />

        <TradeControls
          activeAccount={props.activeAccount}
          onSubmit={props.tryMakingADeal}
          onIntentUpdate={this.onIntentUpdate}
          showIncreaseBidLimitMessage={props.showIncreaseBidLimitMessage}
          disabled={isStockClose || props.tradeControlsDisabled}
          accountType={props.accountType}
          possibleIntervals={props.possibleTradeIntervals}
          currentInterval={props.currentTradeInterval}
          stock={props.stock}
          timeUntilNextLockToggle={props.timeUntilNextLockToggle}
          deals={props.deals}
          isDealAmountValid={props.isDealAmountValid}
          validateDealAmount={props.validateDealAmount}
          refundClickHandler={props.refundClickHandler}
          refundFetching={props.refundFetching}
          time={props.time}
          dealsToCompensate={props.dealsToCompensate}
          onCompensation={props.onCompensation}
          compensationUpdated={props.compensationUpdated}
          compensationCreated={props.compensationCreated}
          onCompensationCreated={props.onCompensationCreated}
        />

        <Tour
          step={[
            "stareAtTheChart",
            "showChart",
            "chartWithProfit",
            "chartWithDeals"
          ]}
          originalRef={this.chartRef}
          disabled
          showTimer
        >
          {({
            next,
            isOriginal,
            step,
            isTourActive,
            isTourMode,
            deal,
            tour,
            startPointsLength
          }) =>
            isOriginal ? (
              !isTourActive &&
              (isStockClose && !isTourMode ? (
                <StockClose
                  stocks={props.stocks}
                  onStockClick={props.onRecommendedStockClick}
                  activeAccount={props.activeAccount}
                  stock={props.stock}
                />
              ) : (
                <Chart
                  animated={props.animated}
                  forwardedRef={this.chartRef}
                  points={isTourMode ? tour.points : void 0}
                  visuals="desktop"
                  deals={props.deals}
                  intent={this.state.intent}
                  stock={props.stock}
                  timeUntilNextLockToggle={props.timeUntilNextLockToggle}
                  dispatch={this.props.dispatch}
                  onEditIndicator={(index, type) =>
                    this.setState({
                      toolbarInstruments: `indicators?${type}:${index}`
                    })
                  }
                  refundClickHandler={props.refundClickHandler}
                  refundFetching={props.refundFetching}
                  time={props.time}
                />
              ))
            ) : step === "showChart" ? (
              <Chart
                animated={props.animated}
                visuals="desktop"
                points={tour.points}
                deals={{
                  type: "SPECIAL TOUR DEAL",
                  deals: tour.deals
                }}
                intent={this.state.intent}
                stock={props.stock}
                timeUntilNextLockToggle={props.timeUntilNextLockToggle}
                dispatch={this.props.dispatch}
                onEditIndicator={(index, type) =>
                  this.setState({
                    toolbarInstruments: `indicators?${type}:${index}`
                  })
                }
                settings={{
                  sentiments: false
                }}
                refundClickHandler={props.refundClickHandler}
                refundFetching={props.refundFetching}
                time={props.time}
              />
            ) : (
              <React.Fragment>
                <Chart
                  interactive={false}
                  animated={props.animated}
                  points={tour.points}
                  visuals="desktop"
                  deals={{
                    type: "SPECIAL TOUR DEAL",
                    deals: tour.deals
                      .slice(-1)
                      .filter(
                        d =>
                          startPointsLength - tour.points.length + d.bTime >= 0
                      )
                  }}
                  intent={null}
                  stock={props.stock}
                  timeUntilNextLockToggle={Infinity}
                  dispatch={this.props.dispatch}
                  onEditIndicator={() => void 0}
                  settings={{
                    indicators: [],
                    timeSpan: (60 * 1000) / 0.8,
                    sentiments: false
                  }}
                  refundClickHandler={props.refundClickHandler}
                  refundFetching={props.refundFetching}
                  time={props.time}
                />
                {startPointsLength - tour.points.length + tour.deals[0].bTime <
                  0 && (
                  <StatelessNews
                    news={[
                      {
                        payload: {
                          op: "operationClosed",
                          id: tour.deals[0].id,
                          stock: tour.deals[0].stock,
                          user_id: 1,
                          profit:
                            tour.deals[0].amount +
                            tour.deals[0].amount *
                              (tour.deals[0].percent / 100),
                          accountId: 1,
                          real: 0,
                          balance: 1,
                          turnover: 0,
                          targetTurnover: false,
                          isBidUp: true,
                          close_time: tour.deals[0].timestamp
                        },
                        isDismissed: false
                      }
                    ]}
                    onDismiss={() => null}
                    soundsEnabled={false}
                    mobile={false}
                  />
                )}
              </React.Fragment>
            )
          }
        </Tour>

        <News />
      </OrderSensitiveDesktopLayout>
    );
  }
}

function OccasionalPopup(props: LayoutProps) {
  if (props.showInsufficientFundsMessage) {
    // A/B test
    return props.userId % 2 ? (
      <PopupDeposit
        isFetching={props.isRefillFetching}
        onRefillDemoClick={props.refillDemoAccount}
        onDismiss={props.dismissInsufficientFundsMessage}
      />
    ) : (
      <InsufficientPopup
        isFetching={props.isRefillFetching}
        onRefillDemoClick={props.refillDemoAccount}
        onDismiss={props.dismissInsufficientFundsMessage}
      />
    );
  }

  if (props.showVerificationRequired) {
    return (
      <VerificationRequiredPopup
        onDismiss={props.dismissVerificationRequiredMessage}
      />
    );
  }

  if (props.showIncreaseBidLimit !== false) {
    return (
      <IncreaseBidLimitPopup onDismiss={props.dismissIncreaseBidLimitMessage} />
    );
  }

  if (props.showTradeIsClosing) {
    return (
      <TradeIsClosingPopup
        onDismiss={props.dismissTradeIsClosing}
        time={props.timeUntilNextLockToggle}
      />
    );
  }

  if (props.showDemoTournament && props.demoTournament) {
    return (
      <DemoTournamentPopup
        tournament={props.demoTournament}
        onDismiss={props.dismissDemoTournament}
      />
    );
  }

  return null;
}

function DesktopLayout({
  history,
  location,
  match,
  ...props
}: $FlowFixMeProps) {
  if (location.pathname !== "/trade" && location.pathname !== "/sightseeing") {
    return <Redirect to="/trade" replace />;
  }

  return (
    <Fragment>
      <DesktopTradeScreen {...props} />
      <OccasionalPopup {...props} />
    </Fragment>
  );
}

export default withErrorCatch(withRouter(DesktopLayout));



// WEBPACK FOOTER //
// ./src/components/Trade/Desktop.js